import type { RelatedContentItem } from '@paction-2.0/ui';
import {
  DEFAULT_OUTCOME_CONTENT,
  DEFAULT_PET_LOSS_QUESTIONS,
  computeOutcomeKey,
  isExternalHref,
  type OutcomeKey,
  type PetLossLocale,
  type ReadinessCallout,
  type ReadinessNextItem,
  type ReadinessQuestion
} from '@paction-2.0/util/petLossReadiness';

/**
 * "Are You Ready After Loss?" — a gentle, non-commercial self-reflection for
 * people grieving a pet. Unlike Paction's other tools, its purpose is NOT to
 * move someone toward a purchase. Most outcomes route the user to grief support
 * (the Association for Pet Loss & Bereavement, APLB) or to adoption/fostering.
 * Only the genuinely-ready outcome surfaces Paction's educational resources, and
 * even then support stays in view.
 *
 * Question set and scoring are placeholders co-developed in spirit with grief
 * professionals; final copy/weights should be reviewed with an APLB counselor.
 */

export type {
  OutcomeKey,
  OutcomeWeights,
  ReadinessOption,
  ReadinessQuestion
} from '@paction-2.0/util/petLossReadiness';

export const QUESTIONS: ReadinessQuestion[] = DEFAULT_PET_LOSS_QUESTIONS;

export const TOTAL_QUESTIONS = QUESTIONS.length;

/**
 * A support resource shown on the results screen. External links (APLB, rescue
 * directories) open in a new tab. We intentionally link to APLB's general
 * support pages — never to a Paction sales/listing page on the support
 * outcomes — keeping this resource non-commercial.
 */
export interface SupportResource {
  /** Display text */
  label: string;
  href: string;
  external?: boolean;
}

export interface Outcome {
  key: OutcomeKey;
  /** Short eyebrow/badge text */
  badge: string;
  heading: string;
  body: string;
  /** Highlighted support callout (APLB / rescues), or null to omit */
  callout: {
    title: string;
    text: string;
    logoSrc?: string;
    links: SupportResource[];
  } | null;
  /** "What's next" cards (RelatedContent). */
  whatsNext: RelatedContentItem[];
}

const S3 = 'https://paction-shared-resources.s3.ca-central-1.amazonaws.com';

export const toRelatedContentItem = (
  item: ReadinessNextItem
): RelatedContentItem => ({
  href: item.url,
  ...(item.imageUrl ? { image: item.imageUrl } : {}),
  category: item.category,
  title: item.title,
  ...(isExternalHref(item.url) ? { openInNewTab: true } : {})
});

const toCallout = (callout: ReadinessCallout): Outcome['callout'] => ({
  title: callout.title,
  text: callout.text,
  ...(callout.logoUrl ? { logoSrc: callout.logoUrl } : {}),
  links: callout.links.map((link) => ({
    label: link.label,
    href: link.url,
    external: isExternalHref(link.url)
  }))
});

export const outcomeFor = (key: OutcomeKey, locale: PetLossLocale): Outcome => {
  const content = DEFAULT_OUTCOME_CONTENT[locale][key];
  return {
    key,
    badge: content.badge,
    heading: content.heading,
    body: content.body,
    callout: content.callout ? toCallout(content.callout) : null,
    whatsNext: content.whatsNext.map(toRelatedContentItem)
  };
};

export const OUTCOMES: Record<OutcomeKey, Outcome> = {
  soon: outcomeFor('soon', 'en'),
  foster: outcomeFor('foster', 'en'),
  ready: outcomeFor('ready', 'en')
};

/**
 * Compute the readiness outcome from the user's answers.
 * Sums each option's weights, then picks the highest-scoring outcome.
 * Ties favour the more cautious outcome (soon > foster > ready) so the tool
 * never nudges a grieving person toward a purchase on a tie.
 */
export function computeOutcome(answers: Record<string, string>): Outcome {
  return OUTCOMES[computeOutcomeKey(QUESTIONS, answers)];
}

/** Related content shown beneath the tool regardless of outcome. */
export const RELATED_CONTENT: RelatedContentItem[] = [
  {
    href: '/cost-calculator',
    image: `${S3}/common/related_cost_calculator.webp`,
    category: 'Tools & Resources',
    title: 'Ultimate Dog Cost Calculator'
  },
  {
    href: '/expert-hub',
    image: `${S3}/common/related_expert_hub.webp`,
    category: 'Tools & Resources',
    title: "Paction's Expert Hub"
  },
  {
    href: '/canine-genetics-for-puppy-shoppers',
    image: `${S3}/common/related_canine_genetics.webp`,
    category: 'Learn',
    title: 'Canine Genetics for Puppy Shoppers'
  }
];
